import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import type { FCC } from '@rikstv/play-common/src/utils/types/typeUtils';
import { ViewMode } from '@rikstv/play-player/src/player.types';

import { THEME_DARK, THEME_DEFAULT } from '../../forces/ui/constants';
import { changeHeaderTheme } from '../../forces/ui/ui.slice';
import { MicroData } from '../../forces/utils/microdata.type';
import { addParameters } from '../../utils/url/url.utils';

import { isTheatreMode } from './coverPage.utils';
import { CoverPageHero } from './CoverPageHero';

import './CoverPage.scss';

interface Props {
  viewMode: ViewMode;
  title: string | string[];
  episodeTitle?: string;
  metatitle: string;
  microdata: MicroData;
  metadataComponent?: JSX.Element;
  itemType?: string;
  image?: string;
  meta?: { property: string; content: string }[];
  providerImageUrl?: string;
  descriptionComponent?: JSX.Element;
  programPointsComponent?: JSX.Element;
  wishlistButton?: JSX.Element;
  playerComponent?: JSX.Element;
  canonicalUrl: string;
}

export const CoverPage: FCC<Props> = ({
  children,
  viewMode,
  meta,
  title,
  episodeTitle,
  metatitle,
  itemType,
  image,
  providerImageUrl,
  metadataComponent,
  microdata,
  descriptionComponent,
  programPointsComponent,
  wishlistButton,
  playerComponent,
  canonicalUrl,
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (isTheatreMode(viewMode)) {
      dispatch(changeHeaderTheme(THEME_DARK));
    } else {
      dispatch(changeHeaderTheme(THEME_DEFAULT));
    }
    return () => {
      dispatch(changeHeaderTheme(THEME_DEFAULT));
    };
  }, [viewMode, dispatch]);
  const absoluteCanonicalUrl = canonicalUrl.startsWith('http') ? canonicalUrl : window.location.origin + canonicalUrl;

  return (
    <div
      itemScope
      itemType={itemType != null ? itemType : 'http://schema.org/BroadcastService'}
      className={classNames('coverpage', { 'viewmode-theatre': isTheatreMode(viewMode) })}>
      <Helmet>
        <title>{metatitle}</title>
        {meta?.map(metaProp => <meta key={metaProp.property} {...metaProp} />)}
        <meta property="og:image" content={image ? addParameters(image, { width: '1200' }) : ''} />
        <link rel="canonical" href={absoluteCanonicalUrl} />
      </Helmet>
      <CoverPageHero
        microdata={microdata}
        title={title}
        episodeTitle={episodeTitle}
        descriptionComponent={descriptionComponent}
        programPointsComponent={programPointsComponent}
        metadataComponent={metadataComponent}
        wishlistButton={wishlistButton}
        image={image}
        providerImageUrl={providerImageUrl}
        playerComponent={playerComponent}
        viewMode={viewMode}
      />
      <div className="coverpage__sections">{children}</div>
    </div>
  );
};
